import {useUserStore} from '~/stores/user'
import {portalHeaders} from '~/config/portal'

const ANALYTICS_EVENT_PATH = '/api/analytics/events'
const ANALYTICS_V2_EVENT_PATH = '/api/analytics/v2/events'
const ANALYTICS_V2_CAPABILITIES_PATH = '/api/analytics/v2/capabilities'

/**
 * BI P2 collector 埋點，對到 reservation-server `POST /api/analytics/events`
 * （見 docs/behavior-insights/implementation-p5.md §1.1、implementation-p2-mysql.md §4）。
 *
 * 刻意不走 `composables/requset.ts` 的 `httpRequest()`：那支預期後端回傳
 * `{error_code, data}` envelope 才能判斷成敗，但這支端點的契約是「成功一律 204
 * 無 body、失敗回 P0001」——用 httpRequest() 解析一個沒有 body 的 204，會在存取
 * `res.error_code` 時對 undefined 拋錯，把每一次成功呼叫都變成一次假錯誤。
 *
 * 這裡是分析訊號，不是業務邏輯：失敗（網路、4xx、5xx）必須整個吞掉，不拋給呼叫端、
 * 不彈窗、不擋既有流程。
 */
export async function postAnalyticsEvent(eventName: string, payload: Record<string, unknown>): Promise<void> {
    try {
        const config = useRuntimeConfig()
        const userStore = useUserStore()
        const headers: Record<string, string> = {...portalHeaders()}
        if (userStore.getToken) {
            headers['Authorization'] = 'Bearer ' + userStore.getToken
        }

        await $fetch(config.public.baseURL + ANALYTICS_EVENT_PATH, {
            method: 'POST',
            credentials: 'include',
            headers,
            body: {event_name: eventName, payload},
        })
    } catch (error) {
        console.warn('[analytics] postAnalyticsEvent 失敗:', error)
    }
}

/** BI-UI v2 collector capabilities 回應（docs/behavior-insights/portal-merchant-ui/api-v2-contract.md）。 */
export interface AnalyticsCapabilitiesV2 {
    enabled: boolean
    schema_version: number
    allowed_events: string[]
}

function analyticsHeaders(): Record<string, string> {
    const userStore = useUserStore()
    const headers: Record<string, string> = {...portalHeaders()}
    if (userStore.getToken) {
        headers['Authorization'] = 'Bearer ' + userStore.getToken
    }
    return headers
}

/** 標準回應 envelope（docs/behavior-insights/portal-merchant-ui/api-v2-contract.md），非原始 data。 */
interface AnalyticsCapabilitiesV2Envelope {
    error_code: string
    error_message: string
    data: AnalyticsCapabilitiesV2
}

/**
 * 讀一次 v2 capabilities（啟用狀態與白名單）。失敗一律回 null——呼叫端必須把 null
 * 當「本頁不送 v2」處理，不得回退重送或雙寫 v1（implementation.md §4.2）。
 */
export async function getAnalyticsCapabilitiesV2(): Promise<AnalyticsCapabilitiesV2 | null> {
    try {
        const config = useRuntimeConfig()
        const res = await $fetch<AnalyticsCapabilitiesV2Envelope>(config.public.baseURL + ANALYTICS_V2_CAPABILITIES_PATH, {
            method: 'GET',
            credentials: 'include',
            headers: analyticsHeaders(),
        })
        if (res.error_code !== '00000' || typeof res.data !== 'object' || res.data === null) {
            return null
        }
        return res.data
    } catch (error) {
        console.warn('[analytics-v2] capabilities 讀取失敗:', error)
        return null
    }
}

/**
 * v2 送出結果，供呼叫端決定重試策略：
 * - ok：成功或重送（server 204）。
 * - client_error：4xx/429，不可重試（重試只會浪費請求或洩漏驗證規則）。
 * - retryable：網路失敗或 5xx，呼叫端可重試一次。
 */
export type AnalyticsSendResultV2 = 'ok' | 'client_error' | 'retryable'

interface FetchErrorLike {
    response?: {status?: number}
    status?: number
}

/**
 * BI-UI v2 collector 單筆事件送出。刻意不走 `composables/requset.ts`，理由同
 * `postAnalyticsEvent`：這支端點成功回 204 無 body，且失敗必須分類給呼叫端做重試判斷，
 * 不能在這裡就把錯誤吞成 undefined。
 */
export async function postAnalyticsEventV2(body: Record<string, unknown>): Promise<AnalyticsSendResultV2> {
    try {
        const config = useRuntimeConfig()
        await $fetch(config.public.baseURL + ANALYTICS_V2_EVENT_PATH, {
            method: 'POST',
            credentials: 'include',
            headers: analyticsHeaders(),
            body,
        })
        return 'ok'
    } catch (error) {
        const e = error as FetchErrorLike
        const status = e?.response?.status ?? e?.status
        if (typeof status === 'number' && status >= 400 && status < 500) {
            return 'client_error'
        }
        return 'retryable'
    }
}
